import React from 'react';

/**
 * Lazy entry points for the six onboarding mirror ROUTE PAGES.
 *
 * The mirror tree (components-onboarding/) is ~35k lines that only school
 * teachers mid-onboarding ever render. Statically importing it shipped the
 * whole tree in the main bundle for every user. These React.lazy seams move
 * the six route pages (and everything only they reach) into async chunks.
 *
 * Deliberately NOT lazy (must exist the instant tour state flips, before
 * any chunk could load): TourGuard, TourExitWarning, OnboardingMainSidebar,
 * CustomizeDraftFeedback — they carry tour anchors/modals and are small.
 *
 * preloadOnboardingPages() warms all six chunks the moment the setup
 * checklist becomes visible (the only gateway into the tours), so the
 * Suspense fallback is never visible in practice. Keep the import()
 * literals inside this one static module so Parcel sees them and the
 * Tier-1 "no stray static import" tripwire has a single place to allow.
 */
const loaders = {
  OnboardingMarkingPage: () =>
    import('../../components-onboarding/Settings/AccountSettingRoot'),
  OnboardingCreateNewTask: () =>
    import('../../components-onboarding/CreateNewTask'),
  OnboardingFeedbacksRoot: () =>
    import('../../components-onboarding/FeedbacksComponents/FeedbacksRoot'),
  OnboardingCommentBanks: () =>
    import('../../components-onboarding/CommentBanks'),
  OnboardingSuccessCriteria: () =>
    import('../../components-onboarding/SuccessCriteria'),
  OnboardingShortAnswers: () =>
    import('../../components-onboarding/ShortAnswers'),
};

// One transparent retry on a failed chunk fetch (transient network blip).
// React.lazy CACHES a rejected payload — without this, any later render
// of the same lazy component re-throws the stale rejection forever.
const lazyWithRetry = (load) =>
  React.lazy(() =>
    load().catch(
      () => new Promise((resolve) => setTimeout(resolve, 1000)).then(load)
    )
  );

export const OnboardingMarkingPage = lazyWithRetry(loaders.OnboardingMarkingPage);
export const OnboardingCreateNewTask = lazyWithRetry(loaders.OnboardingCreateNewTask);
export const OnboardingFeedbacksRoot = lazyWithRetry(loaders.OnboardingFeedbacksRoot);
export const OnboardingCommentBanks = lazyWithRetry(loaders.OnboardingCommentBanks);
export const OnboardingSuccessCriteria = lazyWithRetry(loaders.OnboardingSuccessCriteria);
export const OnboardingShortAnswers = lazyWithRetry(loaders.OnboardingShortAnswers);

let preloaded = false;
export function preloadOnboardingPages() {
  if (preloaded) return;
  preloaded = true;
  Object.values(loaders).forEach((load) => {
    // Failures here are non-fatal: React.lazy retries the import on
    // render, and OnboardingChunkBoundary catches a render-time failure.
    load().catch(() => {
      preloaded = false;
    });
  });
}

/**
 * Error boundary for chunk-load failures — only reachable on a failure
 * path static imports couldn't have (offline mid-session, deploy pruned
 * old chunks), and only after lazyWithRetry's automatic second attempt
 * also failed. React.lazy caches the rejection at that point, so the
 * only real recovery is a reload, which re-fetches index + chunks.
 */
class OnboardingChunkBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error('Onboarding page chunk failed to load', error);
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 12,
            fontFamily: 'var(--font-family-ibm_plex_sans)',
          }}
        >
          <p style={{ color: 'var(--text)', margin: 0 }}>
            This page didn&apos;t load.
          </p>
          <button
            type="button"
            data-tour-target="true"
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px',
              borderRadius: 24,
              border: 'none',
              background: '#7200E0',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Wrap a lazy onboarding route: localized Suspense + chunk boundary. */
export function OnboardingPage({ children }) {
  return (
    <OnboardingChunkBoundary>
      <React.Suspense fallback={null}>{children}</React.Suspense>
    </OnboardingChunkBoundary>
  );
}
